import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClientProvider } from '@tanstack/react-query';
import { ApiClientProvider } from '@weather-story/shared-hooks';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { RouterProvider } from 'react-router';
import { router } from './App';
import { DevVerticalSwitcher } from './components/DevVerticalSwitcher';
import { ErrorBoundary } from './components/ErrorBoundary';
import { VerticalProvider } from './contexts/VerticalContext';
import { I18nProvider } from './i18n';
import { queryClient } from './queryClient';
import { apiClientAdapter } from './services/api';
import './styles/globals.css';
import './styles/fonts.css';

// After a deploy, users with stale index.html reference old chunk hashes that
// no longer exist in S3. CloudFront returns index.html (text/html) for the 404,
// causing "Expected a JavaScript module" MIME errors. Detect this and reload once.
const RELOAD_KEY = '__weathr_chunk_reload';
window.addEventListener(
  'error',
  (e) => {
    if (
      e.message?.includes('Failed to fetch dynamically imported module') ||
      e.message?.includes('Expected a JavaScript module') ||
      (e.target instanceof HTMLScriptElement && e.target.type === 'module')
    ) {
      const reloaded = sessionStorage.getItem(RELOAD_KEY);
      if (!reloaded) {
        sessionStorage.setItem(RELOAD_KEY, '1');
        window.location.reload();
      }
    }
  },
  true
);
// Clear the reload flag on successful load so future deploys can trigger it again
sessionStorage.removeItem(RELOAD_KEY);

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

ReactDOM.createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <I18nProvider>
        <VerticalProvider>
          {import.meta.env.DEV && <DevVerticalSwitcher />}
          <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <HelmetProvider>
              <QueryClientProvider client={queryClient}>
                <ApiClientProvider value={apiClientAdapter}>
                  <RouterProvider router={router} />
                  <Toaster
                    position="bottom-center"
                    toastOptions={{
                      duration: 4000,
                      style: {
                        background: '#1f2937',
                        color: '#fff',
                      },
                    }}
                  />
                </ApiClientProvider>
              </QueryClientProvider>
            </HelmetProvider>
          </GoogleOAuthProvider>
        </VerticalProvider>
      </I18nProvider>
    </ErrorBoundary>
  </StrictMode>
);
